import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Check, ChevronsUpDown, LayoutDashboard, LogOut, UserCog, UserRound } from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { OpcoesDeTema } from './BotaoTema';
import Avatar from './Avatar';
import MeuPerfilDialog from '@/features/usuarios/MeuPerfilDialog';
import { MODULOS, alcancaItem, telaPrincipal, type ItemMenu } from '@/lib/modulos';
import { useAuthStore, type AuthUser } from '@/stores/authStore';
import { useIndicesModal } from '@/stores/indicesModalStore';

const PAPEL_LABEL: Record<AuthUser['papel'], string> = {
  super_admin: 'Administrador da plataforma',
  admin_tenant: 'Administrador',
  usuario: 'Usuário',
};

const CHAVE_MODULO = 'condusvale-modulo';

// Item ativo em azul cheio; os demais ficam neutros e só clareiam no hover.
const ESTILO_ITEM =
  'h-9 gap-[10px] px-sm text-[14px] text-sidebar-foreground hover:text-ink [&_svg]:size-[18px] ' +
  'data-active:bg-sidebar-primary data-active:font-bold data-active:text-sidebar-primary-foreground data-active:shadow-sm ' +
  'data-active:hover:bg-sidebar-primary data-active:hover:text-sidebar-primary-foreground';

const ROTULO_GRUPO = 'px-sm text-[11px] font-bold uppercase tracking-[0.08em] text-muted';

export default function AppSidebar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isMobile, setOpenMobile } = useSidebar();
  const user = useAuthStore((estado) => estado.user);
  const clearAuth = useAuthStore((estado) => estado.clearAuth);
  const abrirIndices = useIndicesModal((estado) => estado.abrir);
  const [perfilAberto, setPerfilAberto] = useState(false);

  const ativos = user?.modulos_ativos ?? [];
  const [modulo, setModulo] = useState(() => {
    const salvo = localStorage.getItem(CHAVE_MODULO);
    const disponivel = (slug: string | null) => !!slug && slug in MODULOS && ativos.includes(slug);
    return disponivel(salvo) ? salvo! : (ativos.find(disponivel) ?? 'cotacao');
  });

  const pode = (permissao?: string) => !permissao || (user?.permissoes ?? []).includes(permissao);
  const alcanca = (item: ItemMenu) => alcancaItem(user, item);

  // A tela aberta diz o módulo: quem chega por link direto ou volta pelo
  // histórico do navegador vê o menu certo, sem precisar trocar à mão.
  const moduloDaRota = Object.entries(MODULOS).find(
    ([slug, dados]) =>
      ativos.includes(slug) && dados.itens.some((item) => pathname === item.path || pathname.startsWith(`${item.path}/`)),
  )?.[0];
  const moduloAtual = moduloDaRota ?? modulo;

  // No celular o menu cobre a tela: escolhida a tela, ele sai da frente.
  const fecharNoCelular = () => {
    if (isMobile) setOpenMobile(false);
  };

  // Trocar de módulo é ir para ele: abre a lista principal, a primeira tela do
  // menu que a pessoa alcança — Cotações, ou Folha por setor.
  const trocarModulo = (slug: string) => {
    setModulo(slug);
    localStorage.setItem(CHAVE_MODULO, slug);

    if (slug === moduloAtual) return;

    const principal = telaPrincipal(user, slug);
    if (principal) {
      navigate(principal);
      fecharNoCelular();
    }
  };

  const sair = () => {
    clearAuth();
    navigate('/login', { replace: true });
  };

  const escolhido = MODULOS[moduloAtual] ?? MODULOS.cotacao;
  const IconeModulo = escolhido.icone;
  const visiveis = escolhido.itens.filter(alcanca);
  // As telas do módulo vêm primeiro; o que abre por cima da tela atual fica à parte.
  const telas = visiveis.filter((item) => !item.modal);
  const consultas = visiveis.filter((item) => item.modal);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="px-md pt-lg pb-sm group-data-[collapsible=icon]:px-sm group-data-[collapsible=icon]:pt-md">
        {/* Aberto, o menu mostra a marca com o nome; recolhido, só o símbolo cabe. A marca leva ao início. */}
        <Link to="/" onClick={fecharNoCelular} aria-label="Início" className="block rounded-md">
          <img
            src="/logo.png"
            alt="Condusvale"
            className="mx-auto hidden size-8 shrink-0 rounded-full group-data-[collapsible=icon]:block"
          />
          <img
            src="/logo-nome.svg"
            alt="Condusvale — Distribuidor Elétrico dos Vales"
            className="mx-auto h-[44px] w-auto max-w-full group-data-[collapsible=icon]:hidden"
          />
        </Link>
        {/* O fio vermelho repete o traço que sublinha o nome na logomarca. */}
        <div
          aria-hidden
          className="mt-md h-px bg-linear-to-r from-transparent via-marca/70 to-transparent group-data-[collapsible=icon]:hidden"
        />
      </SidebarHeader>

      <SidebarContent className="gap-xs">
        <SidebarGroup className="pt-sm">
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    size="lg"
                    aria-label={`Módulo: ${escolhido.nome}. Trocar de módulo`}
                    className="gap-sm border border-sidebar-border bg-surface-subtle/50 px-sm hover:border-accent/40 data-[state=open]:border-accent/40 data-[state=open]:bg-sidebar-accent group-data-[collapsible=icon]:border-0 group-data-[collapsible=icon]:bg-transparent"
                  >
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-accent/10 text-accent">
                      <IconeModulo />
                    </span>
                    <span className="grid min-w-0 flex-1 leading-tight">
                      <span className="text-[11px] text-muted">Módulo</span>
                      <span className="truncate text-[13px] font-bold text-ink">{escolhido.nome}</span>
                    </span>
                    <ChevronsUpDown className="ml-auto text-muted" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  side={isMobile ? 'bottom' : 'right'}
                  align="start"
                  sideOffset={8}
                  className="min-w-[240px]"
                >
                  <DropdownMenuLabel className="text-muted">Módulos</DropdownMenuLabel>
                  {Object.entries(MODULOS).map(([slug, dados]) => {
                    const Icone = dados.icone;
                    const contratado = ativos.includes(slug);

                    return (
                      <DropdownMenuItem
                        key={slug}
                        disabled={!contratado}
                        onClick={() => trocarModulo(slug)}
                        className="gap-sm py-sm"
                      >
                        <span className="flex size-7 shrink-0 items-center justify-center rounded-md border border-border">
                          <Icone size={14} />
                        </span>
                        <span className="flex-1">{dados.nome}</span>
                        {!contratado && <span className="text-[11px] text-muted">Não contratado</span>}
                        {slug === moduloAtual && <Check size={14} className="text-accent" />}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>

        {/* A página inicial não pertence a módulo nenhum: fica logo abaixo do seletor, antes das telas do módulo (U-10). */}
        <SidebarGroup className="py-0">
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip="Início" isActive={pathname === '/'} className={ESTILO_ITEM}>
                <Link to="/" onClick={fecharNoCelular}>
                  <LayoutDashboard />
                  <span>Início</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>

        {telas.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel className={ROTULO_GRUPO}>{escolhido.secao}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="gap-xs">
                {telas.map((item) => {
                  const Icone = item.icone;

                  return (
                    <SidebarMenuItem key={item.path}>
                      <SidebarMenuButton asChild tooltip={item.rotulo} isActive={pathname.startsWith(item.path)} className={ESTILO_ITEM}>
                        <Link to={item.path} onClick={fecharNoCelular}>
                          <Icone />
                          <span>{item.rotulo}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {consultas.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel className={ROTULO_GRUPO}>Consulta rápida</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="gap-xs">
                {consultas.map((item) => {
                  const Icone = item.icone;

                  return (
                    <SidebarMenuItem key={item.path}>
                      <SidebarMenuButton
                        tooltip={item.rotulo}
                        onClick={() => {
                          fecharNoCelular();
                          abrirIndices();
                        }}
                        className={ESTILO_ITEM}
                      >
                        <Icone />
                        <span>{item.rotulo}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Usuários não pertencem a módulo nenhum: a seção aparece em qualquer módulo (U-10). */}
        {pode('usuarios.gerenciar') && (
          <SidebarGroup>
            <SidebarGroupLabel className={ROTULO_GRUPO}>Administração</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="gap-xs">
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Usuários" isActive={pathname.startsWith('/usuarios')} className={ESTILO_ITEM}>
                    <Link to="/usuarios" onClick={fecharNoCelular}>
                      <UserCog />
                      <span>Usuários</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      {user && (
        <SidebarFooter className="border-t border-sidebar-border p-sm">
          <SidebarMenu>
            <SidebarMenuItem>
              {/* A conta mora no rodapé: Meu perfil, tema e Sair. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton size="lg" aria-label="Minha conta" className="gap-sm px-sm data-[state=open]:bg-sidebar-accent">
                    <Avatar nome={user.nome} fotoUrl={user.foto_url} className="ring-2 ring-sidebar" />
                    <span className="grid min-w-0 flex-1 leading-tight">
                      <span className="truncate text-[13px] font-bold text-ink">{user.nome}</span>
                      <span className="truncate text-[11px] text-muted">{PAPEL_LABEL[user.papel]}</span>
                    </span>
                    <ChevronsUpDown className="ml-auto text-muted" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  side={isMobile ? 'top' : 'right'}
                  align="end"
                  sideOffset={8}
                  className="min-w-[240px]"
                >
                  <DropdownMenuLabel className="font-normal">
                    <p className="truncate font-bold text-ink">{user.nome}</p>
                    <p className="truncate text-muted">{user.email}</p>
                    {user.tenant && <p className="truncate text-muted">{user.tenant}</p>}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setPerfilAberto(true)}>
                    <UserRound size={14} />
                    Meu perfil
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <OpcoesDeTema />
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={sair}>
                    <LogOut size={14} />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
          <MeuPerfilDialog aberto={perfilAberto} aoFechar={() => setPerfilAberto(false)} />
        </SidebarFooter>
      )}
    </Sidebar>
  );
}
